import Phaser from 'phaser';
import { GAME } from '../config';
import type { FactionId } from '../../shared/types';
import { FACTIONS, FACTION_COLORS } from '../../shared/factions';
import {
  loadProgress,
  saveProgress,
  getSelectedHeroId,
  setSelectedHeroId,
} from '../../shared/progress';
import {
  PLAYER_TANK_SPRITE_KEYS,
  PLAYER_TANK_SPRITE_URLS,
  HERO_PORTRAIT_KEYS,
  HERO_PORTRAIT_URLS,
  HERO_BUST_KEYS,
  HERO_BUST_URLS,
  HERO_PORTRAIT_GENDER_URLS,
  heroPortraitGenderKeyFor,
  type HeroGender,
} from '../entities/sprite-keys';
import { WEAPON_REGISTRY, DEFAULT_WEAPON_ID } from '../weapons/registry';
import { SLOT0_CONFIG } from '../skills/faction-playstyles';
import { HERO_CARDS } from '../../learn/hero-select';

/**
 * GarageScene — 科技魔幻「車庫家」(boss 2026-06-15「Armored Warfare 式的車庫家」).
 *
 * 概念:大倉庫裡擺著「我已經拿到的戰車/武器」,有「我的家擺著我的收藏」的感覺。
 *   - 背景 = AI 生科技魔幻大倉庫(warehouse.png,藍紫發光展示台 + 武器架)。
 *   - 戰車收藏:5 門派戰車擺在展示台上。已解鎖 = 全彩可點;未解鎖 = 半透明剪影 + 🔒。
 *     目前出戰的戰車置中、放大、打光(聚光燈 + 門派色光環 + idle 漂浮);
 *     點別台已解鎖 → 切換為出戰戰車(寫 selectedHeroId,既有資料,不自造平行狀態)。
 *   - 武器架:右牆把「已拿到的武器」用 emoji icon 排上去(讀 unlockedWeapons)。
 *   - 頂部:meta 金幣(讀 progress.metaCoins)。
 *   - 導覽鈕:⚔ 出戰 / 🗺 闖關 / 🔧 升級基地 / ‹ 返回。
 *
 * 受眾國小三年級(9 歲):大圖示、少字、發光、圓角、明確回饋。
 *
 * 雙軌進度鐵則(CLAUDE.md):本場景**只讀**遊戲軌資料(unlockedHeroes / selectedHeroId /
 *   unlockedWeapons / metaCoins),唯一寫入是 setSelectedHeroId(選出戰戰車,玩遊戲軌「廣度」),
 *   **絕不**碰 factionXP / freeXP / skillLevels / 修煉值,**0 答題彈窗**。
 *
 * 進場/退場:`scene.start('GarageScene')`(由 main.ts 的 'hub' mode 落點 / 各返回入口)。
 *   出戰 → HorizontalBattleScene(帶選中門派 stage 1);闖關 → CampaignMapScene;
 *   升級基地 → MetaShopScene(returnScene='GarageScene');返回 → dispatch ef:return-home。
 */

const HERO_ORDER: readonly FactionId[] = ['celestial', 'runic', 'techno', 'elemental', 'dominion'];

/**
 * 門派玩法定位一句話(boss 2026-06-15「在車庫就看得到選這台會怎麼打」)。
 * 真實文案來源:hero-select.ts 的 HERO_CARDS(英雄名 + tagline)。車庫的價值補完 =
 * 點戰車時把這份「選這台會怎麼打」直接攤在主舞台上,而非只切換出戰戰車。
 * 純查表,0 答題、0 progress 寫入。
 */
const HERO_PLAYSTYLE: Record<FactionId, { heroName: string; tagline: string }> = (() => {
  const out = {} as Record<FactionId, { heroName: string; tagline: string }>;
  for (const card of HERO_CARDS) {
    out[card.id] = { heroName: card.heroName, tagline: card.tagline };
  }
  return out;
})();

/** warehouse 背景 texture key(本場景自帶 preload,避免依賴他場景)。 */
const WAREHOUSE_KEY = 'garage-warehouse';
const WAREHOUSE_URL = 'assets/ai-gen/garage/warehouse.png';

export class GarageScene extends Phaser.Scene {
  /** 目前選中(出戰)的門派。 */
  private heroId: FactionId = 'celestial';
  // hero-gender-wire: which gender portrait the central主角卡 shows. 純展示,
  // scene-local (不寫存檔 → 不碰 save/sync schema),預設女版。
  private _heroGender: HeroGender = 'f';
  /** 已解鎖門派(讀 progress.unlockedHeroes;缺省至少含 selectedHeroId)。 */
  private unlocked: Set<FactionId> = new Set();
  /** 中央主舞台容器(切換戰車時重建)。 */
  private stageContainer: Phaser.GameObjects.Container | null = null;
  /** 側邊收藏列容器(切換時重建以更新選中框)。 */
  private rowContainer: Phaser.GameObjects.Container | null = null;
  /** 右牆武器架容器(換武器時重建以更新高亮 + blurb)。 */
  private weaponRackContainer: Phaser.GameObjects.Container | null = null;
  /** 目前選中(出戰)的主武器 id(唯讀自 progress.selectedWeapon,選武器時更新)。 */
  private weaponId: string = DEFAULT_WEAPON_ID;

  constructor() { super('GarageScene'); }

  preload(): void {
    // warehouse 大圖(本場景背景)。缺檔 → create() 走漸層 fallback。
    try {
      if (!this.textures.exists(WAREHOUSE_KEY)) {
        this.load.image(WAREHOUSE_KEY, WAREHOUSE_URL);
      }
    } catch { /* headless */ }
    // 5 門派玩家戰車 sprite(展示台用)。已載過(從戰鬥/hub 進來)直接跳過。
    try {
      for (const [faction, key] of Object.entries(PLAYER_TANK_SPRITE_KEYS)) {
        if (!this.textures.exists(key)) {
          this.load.image(key, PLAYER_TANK_SPRITE_URLS[faction as FactionId]);
        }
      }
    } catch { /* headless */ }
    // 5 門派英雄立繪(中央展示台「主角卡」用)。缺檔 → 回退坦克展示。
    try {
      for (const [faction, key] of Object.entries(HERO_PORTRAIT_KEYS)) {
        if (!this.textures.exists(key)) {
          this.load.image(key, HERO_PORTRAIT_URLS[faction as FactionId]);
        }
      }
    } catch { /* headless */ }
    // 男女各一版立繪(hero-gender-wire)。缺檔 → 回退無性別版 → 坦克。
    try {
      for (const [key, url] of Object.entries(HERO_PORTRAIT_GENDER_URLS)) {
        if (!this.textures.exists(key)) {
          this.load.image(key, url);
        }
      }
    } catch { /* headless */ }
    // 半身卡(boss 2026-06-20「卡牌用半身」)。缺檔 → 玩法面板不顯示半身,不影響全身 splash。
    try {
      for (const [faction, key] of Object.entries(HERO_BUST_KEYS)) {
        if (!this.textures.exists(key)) {
          this.load.image(key, HERO_BUST_URLS[faction as FactionId]);
        }
      }
    } catch { /* headless */ }
  }

  create(): void {
    const progress = loadProgress();
    this.heroId = progress ? getSelectedHeroId(progress) : 'celestial';
    this.unlocked = this._readUnlocked();
    this.weaponId = this._readSelectedWeapon();

    const W = GAME.width;
    const H = GAME.height;

    this.cameras.main.setBackgroundColor('#0a0726');
    this._drawBackground(W, H);

    // 頂部標題列 + 金幣牌。
    this._buildHeader(W);

    // 中央主舞台(選中戰車打光放大)。
    this._buildHeroStage();

    // 側邊收藏列(5 門派縮圖,點切換)。
    this._buildCollectionRow();

    // 右牆武器架。
    this._buildWeaponRack();

    // 底部導覽鈕。
    this._buildNav(W, H);

    // 進場:整體輕微淡入。
    try {
      this.cameras.main.fadeIn(260, 5, 4, 24);
    } catch { /* headless */ }
  }

  // ── 資料讀取(唯讀;唯一寫入是 selectedHeroId)─────────────────────────────────
  private _readUnlocked(): Set<FactionId> {
    const p = loadProgress();
    const set = new Set<FactionId>();
    const list = p && Array.isArray(p.unlockedHeroes) ? p.unlockedHeroes : null;
    if (list) {
      for (const id of list) {
        if ((HERO_ORDER as readonly string[]).includes(id)) set.add(id as FactionId);
      }
    }
    // 一律確保「目前選中的」算已解鎖(避免空清單時主角變剪影)。
    set.add(this.heroId);
    if (set.size === 0) set.add('celestial');
    return set;
  }

  private _currentCoins(): number {
    const p = loadProgress();
    return p && Number.isFinite(p.metaCoins) ? (p.metaCoins as number) : 0;
  }

  /**
   * 目前出戰主武器 id(讀 progress.selectedWeapon)。未選 / 未知 / 未解鎖 → 回退預設。
   * 沿用既有 selectedWeapon 欄位,不自造平行狀態。
   */
  private _readSelectedWeapon(): string {
    const p = loadProgress();
    const sel = p && typeof p.selectedWeapon === 'string' ? p.selectedWeapon : DEFAULT_WEAPON_ID;
    const owned = this._ownedWeaponIds();
    return owned.includes(sel) ? sel : DEFAULT_WEAPON_ID;
  }

  /**
   * 已擁有的武器 id(registry 順序;永遠含預設武器 + progress.unlockedWeapons)。
   * 給武器架列表用 — 跟既有 weapon-select 畫面同一份「擁有」判準。
   */
  private _ownedWeaponIds(): string[] {
    const p = loadProgress();
    const unlockedWeapons = p && Array.isArray(p.unlockedWeapons) ? p.unlockedWeapons : [];
    const owned = new Set<string>([DEFAULT_WEAPON_ID, ...unlockedWeapons]);
    return Object.keys(WEAPON_REGISTRY).filter((id) => owned.has(id));
  }

  // ── 背景:warehouse 大圖(cover-fit)+ 暗角讓 UI 讀得清 ───────────────────────
  private _drawBackground(W: number, H: number): void {
    let hasBg = false;
    try {
      if (this.textures.exists(WAREHOUSE_KEY)) {
        const img = this.add.image(W / 2, H / 2, WAREHOUSE_KEY).setOrigin(0.5).setDepth(-100);
        const tw = img.width || 1;
        const th = img.height || 1;
        // cover：取較大縮放,鋪滿整張畫布(letterbox 由 Scale.FIT 在外層處理)。
        const scale = Math.max(W / tw, H / th);
        img.setScale(scale);
        hasBg = true;
      }
    } catch { /* headless */ }

    if (!hasBg) {
      // fallback:科技魔幻漸層(藍紫),呼應倉庫色調。
      const g = this.add.graphics().setDepth(-100);
      const top = Phaser.Display.Color.ValueToColor('#1a1248');
      const bot = Phaser.Display.Color.ValueToColor('#070420');
      const steps = 48;
      for (let i = 0; i < steps; i++) {
        const t = i / (steps - 1);
        const c = Phaser.Display.Color.Interpolate.ColorWithColor(top, bot, 100, t * 100);
        g.fillStyle(Phaser.Display.Color.GetColor(c.r, c.g, c.b), 1);
        g.fillRect(0, (H / steps) * i, W, H / steps + 1);
      }
      // 地面展示台暈光。
      const glow = this.add.graphics().setDepth(-99);
      glow.fillStyle(0x6a3fb0, 0.16);
      glow.fillEllipse(W / 2, H * 0.66, W * 0.8, 230);
    }

    // 暗角:四邊壓暗 + 上方標題帶 + 下方鈕帶,讓 UI 從花俏背景跳出來。
    const vg = this.add.graphics().setDepth(-90);
    vg.fillStyle(0x05031a, 0.34);
    vg.fillRect(0, 0, W, H);
    vg.fillStyle(0x05031a, 0.55);
    vg.fillRect(0, 0, W, 70);            // 頂帶
    vg.fillStyle(0x05031a, 0.5);
    vg.fillRect(0, H - 92, W, 92);       // 底帶
  }

  // ── 頂部:標題 + 金幣牌 ───────────────────────────────────────────────────────
  private _buildHeader(W: number): void {
    this.add.text(W / 2, 26, '🛠 我的車庫', {
      fontSize: '28px', color: '#ffe9b8', fontFamily: 'sans-serif', fontStyle: 'bold',
      stroke: '#2a1454', strokeThickness: 6,
    }).setOrigin(0.5).setDepth(60).setShadow(0, 2, '#000000', 6, true, true);
    this.add.text(W / 2, 50, '出戰的戰車與收集到的武器', {
      fontSize: '12px', color: '#b9a9ec', fontFamily: 'sans-serif',
    }).setOrigin(0.5).setDepth(60);

    // 金幣牌(右上,金色發光膠囊)。
    const x = W - 84;
    const y = 30;
    const w = 132;
    const h = 38;
    const g = this.add.graphics().setDepth(60);
    g.fillStyle(0x180f33, 0.92);
    g.fillRoundedRect(x - w / 2, y - h / 2, w, h, 19);
    g.lineStyle(2, 0xffd54f, 0.85);
    g.strokeRoundedRect(x - w / 2, y - h / 2, w, h, 19);
    this.add.text(x - w / 2 + 18, y, '🪙', { fontSize: '19px', fontFamily: 'sans-serif' })
      .setOrigin(0.5).setDepth(61);
    // 車庫不消耗/產出金幣(那發生在戰鬥/基地升級),顯示當前值即可,毋須持有 ref。
    this.add.text(x - w / 2 + 36, y, String(this._currentCoins()), {
      fontSize: '19px', color: '#ffd54f', fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(61);
  }

  // ── 中央主舞台:選中戰車放大 + 聚光燈 + 門派色光環 + idle 漂浮 ─────────────────
  private _buildHeroStage(): void {
    if (this.stageContainer) { this.stageContainer.destroy(); this.stageContainer = null; }
    const W = GAME.width;
    const cx = W / 2;
    const cy = 256;
    const c = this.add.container(0, 0).setDepth(20);
    this.stageContainer = c;

    const fc = FACTION_COLORS[this.heroId];

    // 聚光燈錐(從上方打下,梯形漸亮)。
    const spot = this.add.graphics();
    spot.fillStyle(fc.accent, 0.10);
    spot.fillTriangle(cx, 96, cx - 150, cy + 78, cx + 150, cy + 78);
    spot.fillStyle(0xffffff, 0.05);
    spot.fillTriangle(cx, 110, cx - 96, cy + 70, cx + 96, cy + 70);
    c.add(spot);

    // 旋轉台:同心發光圓盤 + 門派色光環。
    const disc = this.add.graphics();
    disc.fillStyle(fc.primary, 0.22);
    disc.fillEllipse(cx, cy + 86, 268, 70);
    disc.fillStyle(fc.primary, 0.34);
    disc.fillEllipse(cx, cy + 86, 210, 54);
    disc.lineStyle(3, fc.accent, 0.85);
    disc.strokeEllipse(cx, cy + 86, 210, 54);
    disc.lineStyle(2, fc.accent, 0.4);
    disc.strokeEllipse(cx, cy + 86, 262, 68);
    c.add(disc);

    // 光環(戰車後方放射暈光 — 用呼吸 tween 讓「我的主角」活著)。
    const halo = this.add.graphics();
    halo.fillStyle(fc.primary, 0.3);
    halo.fillCircle(cx, cy + 8, 118);
    halo.fillStyle(fc.accent, 0.14);
    halo.fillCircle(cx, cy + 8, 80);
    c.add(halo);
    try {
      this.tweens.add({
        targets: halo, alpha: 0.55, scaleX: 1.06, scaleY: 1.06,
        duration: 1700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    } catch { /* headless */ }

    // 落地陰影。
    const shadow = this.add.ellipse(cx, cy + 96, 170, 30, 0x000000, 0.4);
    c.add(shadow);

    // 主角全身 splash(boss 2026-06-20「大背景才用全身」放大、去框、站轉台上)。缺圖回退戰車。
    const tank = this._makeHeroDisplay(cx, cy, 268);
    if (tank) {
      c.add(tank);
      try {
        this.tweens.add({
          targets: tank, y: cy - 12, duration: 1500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
        });
        this.tweens.add({
          targets: shadow, scaleX: 0.84, alpha: 0.26,
          duration: 1500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
        });
      } catch { /* headless */ }
    }

    // 「出戰中」徽標(移到頭頂上方,不壓臉)。門派名牌移除 —— 左側半身卡已有名字/門派。
    const badge = this.add.graphics();
    badge.fillStyle(fc.primary, 0.95);
    badge.fillRoundedRect(cx - 50, cy - 158, 100, 24, 12);
    badge.lineStyle(2, fc.accent, 0.9);
    badge.strokeRoundedRect(cx - 50, cy - 158, 100, 24, 12);
    c.add(badge);
    c.add(this.add.text(cx, cy - 146, '✦ 出戰中', {
      fontSize: '13px', color: '#fff8e1', fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0.5));

    // 男女切換(hero-gender-wire)。移到轉台下方間隙,純展示 0 寫入,點擊 → 重建舞台。
    this._buildGenderToggle(c, cx, cy + 150);

    // ── 門派玩法資訊面板(boss 2026-06-15:車庫補「選主角」價值)─────────────────
    // 在主舞台左側直接攤開「選這台會怎麼打」:英雄名 + 玩法定位一句話(HERO_CARDS)+
    // slot-0 主動技 icon/名(SLOT0_CONFIG)。真實文案、純展示,0 答題、0 寫入。
    this._buildPlaystylePanel(c, cy);

    // 進場小彈:轉台戰車 scale-in。
    try {
      c.setScale(0.96);
      this.tweens.add({ targets: c, scale: 1, duration: 240, ease: 'Back.easeOut' });
    } catch { /* headless */ }
  }

  /**
   * 主角立繪「展示卡」(boss 2026-06-15「主角放進車庫」)。全身英雄立繪(劍星風 key-art,
   * 含特效背景)裝進門派色相框,置中於展示台。立繪缺檔 → 回退戰車展示(_makeTankDisplay)。
   * 純展示,0 寫入。
   */
  private _makeHeroDisplay(x: number, y: number, targetH: number): Phaser.GameObjects.GameObject | null {
    try {
      // 優先用性別版立繪(hero-gender-wire);缺檔回退無性別版 → 再回退坦克。
      const genderKey = heroPortraitGenderKeyFor(this.heroId, this._heroGender);
      const key = this.textures.exists(genderKey) ? genderKey : HERO_PORTRAIT_KEYS[this.heroId];
      if (this.textures.exists(key)) {
        // boss 2026-06-20「大背景才用全身」:全身立繪 = 中央大 splash,不裝相框,
        // 直接站在轉台聚光燈上(去背透明,門派色 disc/halo 當背景)。半身另放左側卡。
        // ⚠ container 必須建在 (x, y):caller 的漂浮 tween 是 `y: cy-12`,子物件用相對 (0,0)。
        const cont = this.add.container(x, y);
        const probe = this.add.image(0, 0, key).setOrigin(0.5);
        const w = probe.width || 1;
        const h = probe.height || 1;
        probe.setDisplaySize(targetH * (w / h), targetH);
        cont.add(probe);
        return cont;
      }
    } catch { /* headless */ }
    // 立繪缺檔 → 回退既有戰車展示。
    return this._makeTankDisplay(x, y, targetH);
  }

  /**
   * 主角卡下方的「♀ 女 / ♂ 男」切換(hero-gender-wire)。純展示 —— 切換只改顯示的
   * 立繪,絕不碰 factionXP / skillLevels / 傷害(CLAUDE.md 鐵則)。掛進 stage 容器
   * c,隨 _buildHeroStage 重建一起清掉。
   */
  private _buildGenderToggle(c: Phaser.GameObjects.Container, x: number, y: number): void {
    const fc = FACTION_COLORS[this.heroId];
    const opts: { g: HeroGender; label: string }[] = [
      { g: 'f', label: '♀ 女' },
      { g: 'm', label: '♂ 男' },
    ];
    const bw = 64, bh = 30, gap = 10;
    const totalW = opts.length * bw + (opts.length - 1) * gap;
    let bx = x - totalW / 2;
    for (const opt of opts) {
      const cxh = bx + bw / 2;
      const active = opt.g === this._heroGender;
      const g = this.add.graphics();
      g.fillStyle(active ? fc.primary : 0x180f33, active ? 0.95 : 0.7);
      g.fillRoundedRect(bx, y - bh / 2, bw, bh, 15);
      g.lineStyle(2, active ? fc.accent : 0x6b5a96, active ? 0.95 : 0.6);
      g.strokeRoundedRect(bx, y - bh / 2, bw, bh, 15);
      c.add(g);
      c.add(this.add.text(cxh, y, opt.label, {
        fontSize: '15px', color: active ? '#fff8e1' : '#b9a9d6',
        fontFamily: 'sans-serif', fontStyle: 'bold',
      }).setOrigin(0.5));
      const hit = this.add.zone(cxh, y, bw, bh).setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => this._setGender(opt.g));
      c.add(hit);
      bx += bw + gap;
    }
  }

  /** 切換主角性別(純展示,0 寫入)→ 光爆 + 重建舞台。 */
  private _setGender(g: HeroGender): void {
    if (g === this._heroGender) return;
    this._heroGender = g;
    this._swapBurst();
    this._buildHeroStage();
  }

  /**
   * 建一個「展示用戰車」GameObject(sprite 或 fallback 剪影),縮到目標高度、置中。
   * 回傳 null 時 caller 不加(理論上 fallback 一定有東西)。
   */
  private _makeTankDisplay(x: number, y: number, targetH: number): Phaser.GameObjects.GameObject | null {
    const key = PLAYER_TANK_SPRITE_KEYS[this.heroId];
    try {
      if (this.textures.exists(key)) {
        const img = this.add.image(x, y, key).setOrigin(0.5);
        const w = img.width || 1;
        const h = img.height || 1;
        img.setDisplaySize(targetH * (w / h), targetH);
        return img;
      }
    } catch { /* headless */ }
    // fallback:門派色簡易坦克剪影。
    try {
      const fc = FACTION_COLORS[this.heroId];
      const g = this.add.graphics();
      const s = targetH / 200;
      g.fillStyle(fc.primary, 1);
      g.fillRoundedRect(x - 80 * s, y - 6 * s, 160 * s, 56 * s, 12 * s);
      g.fillStyle(fc.accent, 0.9);
      g.fillRoundedRect(x - 46 * s, y - 44 * s, 92 * s, 46 * s, 10 * s);
      g.fillStyle(fc.primary, 1);
      g.fillRect(x + 34 * s, y - 30 * s, 72 * s, 12 * s);
      return g;
    } catch { /* headless */ }
    return null;
  }

  /**
   * 門派玩法資訊卡 — 釘在主舞台左側,科技魔幻全像投影風(門派色描邊 + 半透明深底)。
   * 內容:✦ 玩法定位標題 + 英雄名 + 一句話 tagline + slot-0 主動技 icon/名。
   * 文案全部來自既有真相(HERO_CARDS / SLOT0_CONFIG / FACTIONS),純展示。
   * 加進傳入的 stageContainer `c`,隨換戰車一起重建。
   */
  private _buildPlaystylePanel(c: Phaser.GameObjects.Container, stageCy: number): void {
    const fc = FACTION_COLORS[this.heroId];
    const f = FACTIONS[this.heroId];
    const play = HERO_PLAYSTYLE[this.heroId];
    const slot = SLOT0_CONFIG[this.heroId];
    const heroName = play?.heroName ?? f.displayName;
    const tagline = play?.tagline ?? '';

    // 卡片幾何(boss 2026-06-20「整張半身放進框、別裁」):直幅卡 = 完整半身原圖 + 下玩法資訊。
    const cw = 178;
    const bustH = 208;         // 上方半身像區(直幅,塞整張)
    const ch = bustH + 106;
    const px = 14;             // 左邊距
    const py = stageCy - ch / 2 + 6;

    // 全像投影底卡(深底 + 門派色雙描邊)。
    const panel = this.add.graphics();
    panel.fillStyle(0x0e0930, 0.9);
    panel.fillRoundedRect(px, py, cw, ch, 16);
    panel.lineStyle(2.5, fc.accent, 0.85);
    panel.strokeRoundedRect(px, py, cw, ch, 16);
    panel.lineStyle(1, fc.primary, 0.5);
    panel.strokeRoundedRect(px + 3, py + 3, cw - 6, ch - 6, 13);
    c.add(panel);

    // ── 上方半身像(boss 2026-06-20「卡牌用半身」)。門派色襯底 + 半身圖 fit + 金邊。缺檔→不顯示。
    const bustTop = py + 6;
    const ig = this.add.graphics();
    ig.fillStyle(fc.primary, 0.18);
    ig.fillRoundedRect(px + 6, bustTop, cw - 12, bustH, { tl: 12, tr: 12, bl: 4, br: 4 });
    c.add(ig);
    try {
      const bustKey = HERO_BUST_KEYS[this.heroId];
      if (this.textures.exists(bustKey)) {
        const bust = this.add.image(px + cw / 2, bustTop + 3, bustKey).setOrigin(0.5, 0);
        const bw = bust.width || 1;
        const bh = bust.height || 1;
        const sc = Math.min((cw - 18) / bw, (bustH - 6) / bh);
        bust.setDisplaySize(bw * sc, bh * sc);
        c.add(bust);
      }
    } catch { /* headless */ }
    const bfr = this.add.graphics();
    bfr.lineStyle(1.5, fc.accent, 0.7);
    bfr.strokeRoundedRect(px + 6, bustTop, cw - 12, bustH, { tl: 12, tr: 12, bl: 4, br: 4 });
    c.add(bfr);

    // 投影呼吸光暈(讓卡片「活著」,呼應主舞台 halo 節奏)。
    try {
      this.tweens.add({
        targets: panel, alpha: 0.82, duration: 1700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    } catch { /* headless */ }

    let ty = bustTop + bustH + 16;
    // 英雄名(大字 + 門派 emoji)。
    c.add(this.add.text(px + 14, ty, `${f.emoji} ${heroName}`, {
      fontSize: '19px', color: '#ffffff', fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0, 0.5));
    ty += 22;

    // 門派名(小字、門派色)。
    c.add(this.add.text(px + 14, ty, f.displayName, {
      fontSize: '12px', color: fc.primaryHex === '#6a1b9a' ? '#c9a8ff' : fc.accentHex,
      fontFamily: 'sans-serif',
    }).setOrigin(0, 0.5));
    ty += 22;

    // 一句話玩法(可換行,撐滿卡寬)。
    c.add(this.add.text(px + 14, ty, tagline, {
      fontSize: '13px', color: '#e7defc', fontFamily: 'sans-serif',
      wordWrap: { width: cw - 28 }, lineSpacing: 2,
    }).setOrigin(0, 0));
    ty += 38;

    // slot-0 主動技列(icon 圓底 + 「主動技:名稱」)。
    const skillY = py + ch - 22;
    const sg = this.add.graphics();
    sg.fillStyle(slot.color, 0.9);
    sg.fillCircle(px + 26, skillY, 14);
    sg.lineStyle(2, 0xffffff, 0.5);
    sg.strokeCircle(px + 26, skillY, 14);
    c.add(sg);
    c.add(this.add.text(px + 26, skillY, slot.icon, { fontSize: '16px', fontFamily: 'sans-serif' })
      .setOrigin(0.5));
    c.add(this.add.text(px + 46, skillY, `主動技 ${slot.label}`, {
      fontSize: '13px', color: '#ffe9b8', fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0, 0.5));
  }

  // ── 側邊收藏列:5 門派縮圖(展示台),點切換出戰戰車 ───────────────────────────
  private _buildCollectionRow(): void {
    if (this.rowContainer) { this.rowContainer.destroy(); this.rowContainer = null; }
    const W = GAME.width;
    const c = this.add.container(0, 0).setDepth(30);
    this.rowContainer = c;

    const slotW = 108;
    const gap = 8;
    const total = slotW * HERO_ORDER.length + gap * (HERO_ORDER.length - 1);
    const startX = W / 2 - total / 2 + slotW / 2;
    const y = 470;

    HERO_ORDER.forEach((hero, i) => {
      const x = startX + i * (slotW + gap);
      const selected = hero === this.heroId;
      const unlocked = this.unlocked.has(hero);
      const fc = FACTION_COLORS[hero];
      const f = FACTIONS[hero];

      // 展示台底座(圓角面板;選中 → 門派色亮邊上浮)。
      const yOff = selected ? -6 : 0;
      const panel = this.add.graphics();
      panel.fillStyle(selected ? 0x241a52 : 0x15102e, selected ? 0.95 : 0.8);
      panel.fillRoundedRect(x - slotW / 2, y - 48 + yOff, slotW, 96, 14);
      // 台座發光底盤
      panel.fillStyle(unlocked ? fc.primary : 0x2a2440, unlocked ? 0.5 : 0.5);
      panel.fillEllipse(x, y + 30 + yOff, slotW - 26, 18);
      panel.lineStyle(selected ? 3 : 1.5, selected ? fc.accent : (unlocked ? fc.primary : 0x3a3358), selected ? 1 : 0.7);
      panel.strokeRoundedRect(x - slotW / 2, y - 48 + yOff, slotW, 96, 14);
      c.add(panel);

      // 縮圖戰車 / 剪影。
      const key = PLAYER_TANK_SPRITE_KEYS[hero];
      let thumb: Phaser.GameObjects.GameObject | null = null;
      try {
        if (this.textures.exists(key)) {
          const img = this.add.image(x, y - 4 + yOff, key).setOrigin(0.5);
          const w = img.width || 1;
          const h = img.height || 1;
          const th = 58;
          img.setDisplaySize(th * (w / h), th);
          if (!unlocked) {
            // 未解鎖 → 暗剪影(tint 黑 + 半透明)。
            try { img.setTint(0x1a1430); } catch { /* noop */ }
            img.setAlpha(0.55);
          }
          thumb = img;
        }
      } catch { /* headless */ }
      if (!thumb) {
        // emoji fallback 縮圖。
        const e = this.add.text(x, y - 4 + yOff, f.emoji, { fontSize: '34px', fontFamily: 'sans-serif' })
          .setOrigin(0.5);
        if (!unlocked) e.setAlpha(0.45);
        c.add(e);
      } else {
        c.add(thumb);
      }

      // 鎖頭(未解鎖)。
      if (!unlocked) {
        c.add(this.add.text(x, y - 6 + yOff, '🔒', { fontSize: '26px', fontFamily: 'sans-serif' })
          .setOrigin(0.5));
      }

      // 門派名(底部小字)。
      c.add(this.add.text(x, y + 36 + yOff, f.displayName, {
        fontSize: '11px', color: selected ? '#fff3d6' : (unlocked ? '#c9badf' : '#6b6488'),
        fontFamily: 'sans-serif', fontStyle: selected ? 'bold' : 'normal',
      }).setOrigin(0.5));

      // 選中勾標。
      if (selected) {
        const tick = this.add.graphics();
        tick.fillStyle(fc.accent, 1);
        tick.fillCircle(x + slotW / 2 - 14, y - 48 + yOff + 14, 11);
        c.add(tick);
        c.add(this.add.text(x + slotW / 2 - 14, y - 48 + yOff + 14, '✓', {
          fontSize: '14px', color: '#1a0d33', fontFamily: 'sans-serif', fontStyle: 'bold',
        }).setOrigin(0.5));
      }

      // 命中區:點 → 解鎖則切換出戰戰車;未解鎖則 toast。
      const hit = this.add.zone(x, y + yOff, slotW, 96).setOrigin(0.5);
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerup', () => this._onPickHero(hero));
      c.add(hit);
    });
  }

  /** 點側邊縮圖:已解鎖 → 設為出戰戰車並重建主舞台;未解鎖 → 提示。 */
  private _onPickHero(hero: FactionId): void {
    if (!this.unlocked.has(hero)) {
      this._flashToast(`${FACTIONS[hero].displayName} 還沒解鎖`);
      return;
    }
    if (hero === this.heroId) return;
    // 寫入既有 selectedHeroId(玩遊戲軌「廣度」,不碰修煉值)。
    const p = loadProgress();
    if (p) {
      setSelectedHeroId(p, hero);
      saveProgress(p);
    }
    this.heroId = hero;
    // 換台閃光 + 重建主舞台 / 收藏列。
    this._swapBurst();
    this._buildHeroStage();
    this._buildCollectionRow();
  }

  /** 換出戰戰車的中央光爆,強化「換我的主角了」的儀式感。 */
  private _swapBurst(): void {
    const cx = GAME.width / 2;
    const cy = 264;
    const fc = FACTION_COLORS[this.heroId];
    try {
      const ring = this.add.graphics().setDepth(40);
      ring.lineStyle(5, fc.accent, 0.9);
      ring.strokeCircle(cx, cy, 60);
      this.tweens.add({
        targets: ring, scaleX: 2.4, scaleY: 2.4, alpha: 0,
        duration: 520, ease: 'Cubic.easeOut',
        onComplete: () => { try { ring.destroy(); } catch { /* noop */ } },
      });
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2;
        const star = this.add.text(cx, cy, '✦', {
          fontSize: '18px', color: '#ffe9a8', fontFamily: 'sans-serif',
        }).setOrigin(0.5).setDepth(41);
        this.tweens.add({
          targets: star, x: cx + Math.cos(a) * 70, y: cy + Math.sin(a) * 70,
          alpha: 0, scale: 0.4, duration: 500, ease: 'Cubic.easeOut',
          onComplete: () => { try { star.destroy(); } catch { /* noop */ } },
        });
      }
    } catch { /* headless */ }
  }

  // ── 右牆武器架:已拿到的武器 icon,點下去換主武器 + 顯示玩法 ──────────────────
  /**
   * 武器架(boss 2026-06-15:車庫補「選武器」)。列出已擁有武器,點一把 →
   * 設為出戰主武器(寫既有 selectedWeapon,沿用 weapon-select 同款判準),
   * 高亮選中那把 + 底部 blurb 顯示「這把怎麼打」。0 答題、不碰修煉值/戰力欄位。
   */
  private _buildWeaponRack(): void {
    if (this.weaponRackContainer) { this.weaponRackContainer.destroy(); this.weaponRackContainer = null; }
    const c = this.add.container(0, 0).setDepth(25);
    this.weaponRackContainer = c;

    const ids = this._ownedWeaponIds();
    const selectedWeapon = this.weaponId;

    const panelW = 72;
    const x = GAME.width - panelW / 2 - 8;
    const topY = 96;
    const slot = 56;

    // 牆面板(科技架)。
    const wall = this.add.graphics();
    const wallH = Math.max(slot * ids.length + 44, 120);
    wall.fillStyle(0x120c2e, 0.86);
    wall.fillRoundedRect(x - panelW / 2, topY - 32, panelW, wallH, 12);
    wall.lineStyle(2, 0x4ce6d6, 0.5);
    wall.strokeRoundedRect(x - panelW / 2, topY - 32, panelW, wallH, 12);
    c.add(wall);
    c.add(this.add.text(x, topY - 20, '🗡 武器架', {
      fontSize: '11px', color: '#9af0e8', fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0.5));

    ids.forEach((id, i) => {
      const meta = WEAPON_REGISTRY[id];
      const y = topY + 12 + i * slot;
      const isActive = id === selectedWeapon;
      // icon 底座(選中 → 青亮高亮 + 白描邊 + ✓)。
      const g = this.add.graphics();
      g.fillStyle(isActive ? 0x2fd6c6 : 0x1f1840, isActive ? 0.92 : 0.8);
      g.fillRoundedRect(x - 26, y - 23, 52, 46, 10);
      g.lineStyle(isActive ? 3 : 1.5, isActive ? 0xffffff : 0x4ce6d6, isActive ? 1 : 0.5);
      g.strokeRoundedRect(x - 26, y - 23, 52, 46, 10);
      c.add(g);
      c.add(this.add.text(x, y, meta.emoji, { fontSize: '25px', fontFamily: 'sans-serif' })
        .setOrigin(0.5));

      // 選中勾標(右上角)。
      if (isActive) {
        const tick = this.add.graphics();
        tick.fillStyle(0xffffff, 1);
        tick.fillCircle(x + 20, y - 18, 8);
        c.add(tick);
        c.add(this.add.text(x + 20, y - 18, '✓', {
          fontSize: '11px', color: '#0c6b62', fontFamily: 'sans-serif', fontStyle: 'bold',
        }).setOrigin(0.5));
      }

      // 命中區:點 → 換主武器。
      const hit = this.add.zone(x, y, 52, 46).setOrigin(0.5);
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => {
        try { this.tweens.add({ targets: g, scaleX: 0.9, scaleY: 0.9, duration: 80, yoyo: true }); } catch { /* noop */ }
      });
      hit.on('pointerup', () => this._onPickWeapon(id));
      c.add(hit);
    });

    // 無已擁有武器時(理論不會,因為含預設)補一個圖示。
    if (ids.length === 0) {
      c.add(this.add.text(x, topY + 16, '🔫', { fontSize: '24px', fontFamily: 'sans-serif' })
        .setOrigin(0.5));
    }

    // 底部武器玩法 blurb(選中那把「怎麼打」一句話)。
    this._buildWeaponBlurb();
  }

  /**
   * 武器玩法說明條 — 釘在畫面底部、出戰鈕上方。顯示選中武器名 + 特色一句話(stats.special)。
   * 真實文案來自 WEAPON_REGISTRY,純展示。每次換武器跟著武器架一起重建。
   */
  private _buildWeaponBlurb(): void {
    const meta = WEAPON_REGISTRY[this.weaponId] ?? WEAPON_REGISTRY[DEFAULT_WEAPON_ID];
    const cw = 320;
    const ch = 40;
    const cx = GAME.width - 8 - cw / 2;
    const cy = GAME.height - 116;

    const g = this.add.graphics();
    g.fillStyle(0x0e2330, 0.9);
    g.fillRoundedRect(cx - cw / 2, cy - ch / 2, cw, ch, 12);
    g.lineStyle(2, 0x4ce6d6, 0.8);
    g.strokeRoundedRect(cx - cw / 2, cy - ch / 2, cw, ch, 12);
    if (this.weaponRackContainer) this.weaponRackContainer.add(g);

    const txt = this.add.text(cx - cw / 2 + 14, cy, `${meta.emoji} ${meta.displayName}　${meta.stats.special}`, {
      fontSize: '13px', color: '#bff5ee', fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    if (this.weaponRackContainer) this.weaponRackContainer.add(txt);
  }

  /**
   * 點武器架:設為出戰主武器(寫既有 selectedWeapon)。沿用 weapon-select 同款規則 —
   * 只寫 selectedWeapon(玩遊戲軌),絕不碰戰力欄位。已是當前 → 不重畫。
   */
  private _onPickWeapon(id: string): void {
    if (id === this.weaponId) return;
    if (!this._ownedWeaponIds().includes(id)) {
      this._flashToast('還沒拿到這把武器');
      return;
    }
    const p = loadProgress();
    if (p) {
      p.selectedWeapon = id;
      saveProgress(p);
    }
    this.weaponId = id;
    this._weaponSwapBurst();
    this._buildWeaponRack();
  }

  /** 換武器的小光環(青色,呼應武器架配色),強化「換武器」回饋。 */
  private _weaponSwapBurst(): void {
    const cx = GAME.width - 44;
    const cy = GAME.height - 116;
    try {
      const ring = this.add.graphics().setDepth(70);
      ring.lineStyle(4, 0x4ce6d6, 0.9);
      ring.strokeCircle(cx, cy, 26);
      this.tweens.add({
        targets: ring, scaleX: 2.2, scaleY: 2.2, alpha: 0,
        duration: 460, ease: 'Cubic.easeOut',
        onComplete: () => { try { ring.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
  }

  // ── 底部導覽鈕 ────────────────────────────────────────────────────────────────
  private _buildNav(W: number, H: number): void {
    const y = H - 46;
    // 主鈕「⚔ 出戰」(寬、金色發光);其餘為次鈕。
    this._bigButton(W / 2 - 232, y, 188, 60, '⚔ 出戰', 0xf5b53d, 0xffe09a, '#3d1a00', true, () => this._onBattle());
    this._bigButton(W / 2 - 30, y, 138, 56, '🗺 闖關', 0x5c7cff, 0xa9bcff, '#0d1240', false, () => this._onCampaign());
    this._bigButton(W / 2 + 128, y, 150, 56, '🔧 升級基地', 0x9c6bff, 0xd2b3ff, '#1a0d2e', false, () => this._onUpgradeBase());

    // 返回(左上角小膠囊)。
    this._cornerButton(58, 30, 88, 34, '‹ 返回', 0x2a2448, 0x8a7fd0, () => this._onBack());
  }

  /**
   * 大圓角發光鈕(底部導覽)。primary=true 給金色脈動光暈(吸引點擊)。
   */
  private _bigButton(
    cx: number, cy: number, w: number, h: number, label: string,
    fill: number, stroke: number, textColor: string, primary: boolean,
    onClick: () => void,
  ): void {
    const depth = 50;
    if (primary) {
      const halo = this.add.graphics().setDepth(depth - 1);
      halo.fillStyle(fill, 0.3);
      halo.fillRoundedRect(cx - w / 2 - 8, cy - h / 2 - 8, w + 16, h + 16, 20);
      try {
        this.tweens.add({
          targets: halo, alpha: 0.08, duration: 1100, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
        });
      } catch { /* headless */ }
    }
    const g = this.add.graphics().setDepth(depth);
    const draw = (c1: number, c2: number) => {
      g.clear();
      g.fillStyle(c1, 1);
      g.fillRoundedRect(cx - w / 2, cy - h / 2, w, h, 16);
      g.fillStyle(c2, 0.45);
      g.fillRoundedRect(cx - w / 2, cy - h / 2, w, h * 0.5, { tl: 16, tr: 16, bl: 0, br: 0 });
      g.lineStyle(2.5, stroke, 0.95);
      g.strokeRoundedRect(cx - w / 2, cy - h / 2, w, h, 16);
    };
    draw(fill, stroke);
    const txt = this.add.text(cx, cy, label, {
      fontSize: primary ? '24px' : '18px', color: textColor, fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(depth + 1);

    const hit = this.add.zone(cx, cy, w, h).setOrigin(0.5).setDepth(depth + 2);
    hit.setInteractive({ useHandCursor: true });
    hit.on('pointerover', () => { try { this.tweens.add({ targets: [g, txt], scaleX: 1, scaleY: 1, duration: 1 }); } catch { /* noop */ } });
    hit.on('pointerdown', () => {
      try { this.tweens.add({ targets: txt, scale: 0.92, duration: 80, yoyo: true }); } catch { /* noop */ }
    });
    hit.on('pointerup', onClick);
  }

  /** 角落小膠囊鈕(返回)。 */
  private _cornerButton(
    cx: number, cy: number, w: number, h: number, label: string,
    fill: number, stroke: number, onClick: () => void,
  ): void {
    const g = this.add.graphics().setDepth(60);
    g.fillStyle(fill, 0.95);
    g.fillRoundedRect(cx - w / 2, cy - h / 2, w, h, 12);
    g.lineStyle(2, stroke, 0.85);
    g.strokeRoundedRect(cx - w / 2, cy - h / 2, w, h, 12);
    this.add.text(cx, cy, label, {
      fontSize: '14px', color: '#e8e2ff', fontFamily: 'sans-serif', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(61);
    const hit = this.add.zone(cx, cy, w, h).setOrigin(0.5).setDepth(62);
    hit.setInteractive({ useHandCursor: true });
    hit.on('pointerup', onClick);
  }

  /** 短暫 toast(置中下方)。 */
  private _flashToast(msg: string): void {
    const t = this.add.text(GAME.width / 2, GAME.height - 110, msg, {
      fontSize: '15px', color: '#ffffff', fontFamily: 'sans-serif', fontStyle: 'bold',
      backgroundColor: '#3d2a66', padding: { x: 14, y: 8 },
    }).setOrigin(0.5).setDepth(80);
    try {
      this.tweens.add({
        targets: t, alpha: 0, y: GAME.height - 128, duration: 1600, delay: 500,
        onComplete: () => { try { t.destroy(); } catch { /* noop */ } },
      });
    } catch { try { t.destroy(); } catch { /* noop */ } }
  }

  // ── 導覽 actions(只 navigate,0 題目;不破壞既有戰鬥/闖關流程)──────────────────
  /**
   * ⚔ 出戰 — 直接進戰鬥(帶選中門派當 stage 1 主題)。沿用 CampaignMapScene /
   * HubScene 進戰鬥同款:dispatch ef:enter-battle 重掛技能按鈕,再 start
   * HorizontalBattleScene。鐵則:只 navigate,不寫戰力。
   */
  private _onBattle(): void {
    const go = () => {
      try { window.dispatchEvent(new CustomEvent('ef:enter-battle')); } catch { /* noop */ }
      try {
        this.scene.start('HorizontalBattleScene', {
          hubFactionId: this.heroId,
          stage: 1,
        });
      } catch { /* noop */ }
    };
    this._fadeThen(go);
  }

  /** 🗺 闖關 — 進線性闖關地圖(既有 CampaignMapScene)。 */
  private _onCampaign(): void {
    this._fadeThen(() => { try { this.scene.start('CampaignMapScene'); } catch { /* noop */ } });
  }

  /** 🔧 升級基地 — 開指揮官基地永久升級商店(returnScene 回車庫)。 */
  private _onUpgradeBase(): void {
    this._fadeThen(() => {
      try { this.scene.start('MetaShopScene', { heroId: this.heroId, returnScene: 'GarageScene' }); } catch { /* noop */ }
    });
  }

  /** ‹ 返回 — 回學習堡壘(main.ts 接 ef:return-home → learn-portal)。 */
  private _onBack(): void {
    this._fadeThen(() => { try { window.dispatchEvent(new Event('ef:return-home')); } catch { /* noop */ } });
  }

  /** camera fade-out → run go();無 camera 直接執行(headless 安全)。 */
  private _fadeThen(go: () => void): void {
    try {
      const cam = this.cameras?.main;
      const fadeOut = (cam as unknown as { fadeOut?: (ms: number, r?: number, g?: number, b?: number) => void })?.fadeOut;
      if (cam && typeof fadeOut === 'function') {
        fadeOut.call(cam, 220, 0, 0, 0);
        const once = (cam as unknown as { once?: (e: string, cb: () => void) => void }).once;
        if (typeof once === 'function') once.call(cam, 'camerafadeoutcomplete', go);
        else go();
      } else { go(); }
    } catch { go(); }
  }
}
